import axios from 'axios';

const apiBaseURL = process.env.VUE_APP_API_BASE_URL || 'https://api.swervetech.co.ke/api';
const appContext = 'mail';

const clearStoredAuth = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('token_type');
  localStorage.removeItem('expires_in');
  localStorage.removeItem('requires_2fa_pending');
  localStorage.removeItem('session_locked');
  localStorage.removeItem('session_lock_redirect');
  localStorage.removeItem('masked_phone');
  localStorage.removeItem('super_admin_device');
};

const notifyInvalidAuth = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mail-auth-invalid'));
  }
};

if (process.env.NODE_ENV !== 'production') {
  console.info(`[Mail API] baseURL: ${apiBaseURL}`);
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: apiBaseURL, // Laravel API base URL
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-App-Context': appContext,
  }
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    config.headers['X-App-Context'] = appContext;

    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const resp = error.response;

    if (!resp) {
      return Promise.reject(error);
    }

    // Handle 2FA required error (403 with specific error code)
    if (resp.status === 403 && resp.data?.error_code === 'REQUIRES_2FA') {
      // Don't auto-redirect for API calls, let components handle it
      window.dispatchEvent(new CustomEvent('requires-2fa', {
        detail: { redirect_to: resp.data.redirect_to }
      }));
      return Promise.reject(error);
    }

    if (resp.status === 401) {
      const url = originalRequest.url || '';
      if (
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/forgot-password') ||
        url.includes('/auth/refresh') ||
        url.includes('/v1/super-admin/login') ||
        url.includes('/v1/mail/super-admin/login')
      ) {
        return Promise.reject(error);
      }

      if (originalRequest._retry) {
        clearStoredAuth();
        notifyInvalidAuth();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        const refreshRes = await api.post('/auth/refresh');
        const { access_token } = (refreshRes.data && refreshRes.data.data) || {};
        if (access_token) {
          localStorage.setItem('access_token', access_token);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
        clearStoredAuth();
        notifyInvalidAuth();
      } catch (refreshError) {
        clearStoredAuth();
        notifyInvalidAuth();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
